import { useEffect, useMemo, useState } from "react";
import { Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ModuleBlock = ({ group, selected, toggle, toggleAll, disabled }) => {
  const codes = group.permissions.map((p) => p.code);
  const count = codes.filter((c) => selected.has(c)).length;
  return (
    <div className="rounded-xl border border-[#e4e8e1] bg-white" data-testid={`perm-module-${group.module}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#f0f2ee] bg-[#fafbf9] rounded-t-xl">
        <span className="font-semibold text-sm">{group.label} <span className="text-xs font-normal text-[#5b6656]">{count}/{codes.length}</span></span>
        {!disabled && <button data-testid={`perm-module-toggle-${group.module}`} className="text-xs font-semibold text-[#258F00]" onClick={() => toggleAll(codes, count < codes.length)}>{count < codes.length ? "Marcar todo" : "Quitar todo"}</button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 p-4">
        {group.permissions.map((p) => (
          <label key={p.code} className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox data-testid={`perm-${p.code}`} disabled={disabled} checked={selected.has(p.code)} onCheckedChange={() => toggle(p.code)} className="mt-0.5" />
            <span>{p.label}{p.sensitive && <Lock size={11} className="inline ml-1 text-[#c9a400]" />}<span className="block font-mono text-[10px] text-[#8a9584]">{p.code}</span></span>
          </label>
        ))}
      </div>
    </div>
  );
};

const RoleEditor = ({ role, groups, canEdit, onSaved }) => {
  const [f, setF] = useState({ name: role.name, description: role.description });
  const [sel, setSel] = useState(new Set(role.permissions));
  useEffect(() => { setF({ name: role.name, description: role.description }); setSel(new Set(role.permissions)); }, [role]);
  const toggle = (c) => { const s = new Set(sel); s.has(c) ? s.delete(c) : s.add(c); setSel(s); };
  const toggleAll = (codes, on) => { const s = new Set(sel); codes.forEach((c) => (on ? s.add(c) : s.delete(c))); setSel(s); };
  const save = async () => {
    try { await api.patch(`/admin/roles/${role.id}`, { ...f, permissions: [...sel] }); toast.success("Rol actualizado"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async () => {
    try { await api.post(`/admin/roles/${role.id}/${role.status === "activo" ? "deactivate" : "activate"}`); toast.success("Estado actualizado"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5" data-testid="role-editor">
      <div className="syc-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-xs rounded bg-[#eef6e8] text-[#1c5f00] px-2 py-1">{role.code}</span>
          <StatusBadge status={role.status} label={role.status === "activo" ? "Activo" : "Inactivo"} />
          {role.is_system && <span className="text-xs text-[#5b6656]">Rol base del sistema</span>}
          <span className="text-xs text-[#5b6656]">{role.users_count} usuarios</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nombre"><Input data-testid="role-name-input" disabled={!canEdit} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Descripción"><Input data-testid="role-description-input" disabled={!canEdit} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="save-role-btn" className="rounded-full" onClick={save}>Guardar ({sel.size} privilegios)</Button>
            {role.code !== "ADMIN_SISTEMA" && <Button data-testid="toggle-role-status-btn" variant="outline" className="rounded-full" onClick={setStatus}>{role.status === "activo" ? "Desactivar rol" : "Activar rol"}</Button>}
          </div>
        )}
        <p className="text-xs text-[#5b6656]"><Lock size={11} className="inline text-[#c9a400]" /> Privilegio sensible (información económica, remuneraciones o datos personales).</p>
      </div>
      {groups.map((g) => <ModuleBlock key={g.module} group={g} selected={sel} toggle={toggle} toggleAll={toggleAll} disabled={!canEdit} />)}
    </div>
  );
};

const NewRoleDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({ code: "", name: "", description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/roles", { ...f, permissions: [] }); toast.success("Rol creado"); setF({ code: "", name: "", description: "" }); onCreated(data); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-role-dialog">
        <DialogHeader><DialogTitle>Nuevo rol</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Código" hint="Mayúsculas, números y guion bajo. Ej: JEFE_TERRENO"><Input data-testid="new-role-code-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} required /></Field>
          <Field label="Nombre"><Input data-testid="new-role-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <Field label="Descripción"><Input data-testid="new-role-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <DialogFooter><Button data-testid="new-role-submit-btn" className="rounded-full">Crear rol</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Roles() {
  const { can } = useAuth();
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/roles").then((r) => { setRoles(r.data); setActiveId((id) => id || r.data[0]?.id); });
  useEffect(() => { load(); api.get("/admin/permissions").then((r) => setGroups(r.data)); }, []);
  const active = useMemo(() => roles.find((r) => r.id === activeId), [roles, activeId]);
  const canEdit = can("admin.roles.gestionar");

  return (
    <div data-testid="roles-page">
      <PageHeader eyebrow="Administración" title="Roles y Privilegios" subtitle="Usuario → Rol → Privilegio → Alcance → Recurso. Los cambios cierran las sesiones de los usuarios afectados al reasignar roles."
        actions={canEdit && <Button data-testid="create-role-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Nuevo rol</Button>} />
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="syc-card p-2 h-fit lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto" data-testid="roles-list">
          {roles.map((r) => (
            <button key={r.id} data-testid={`role-item-${r.code}`} onClick={() => setActiveId(r.id)}
              className={`text-left rounded-lg px-3 py-2.5 shrink-0 transition-colors ${r.id === activeId ? "bg-[#258F00] text-white" : "hover:bg-[#F7F7F7]"} ${r.status !== "activo" ? "opacity-60" : ""}`}>
              <div className="text-sm font-semibold whitespace-nowrap">{r.name}</div>
              <div className={`text-[11px] ${r.id === activeId ? "text-white/80" : "text-[#8a9584]"}`}>{r.permissions.length} privilegios · {r.users_count} usuarios</div>
            </button>
          ))}
        </div>
        {active && <RoleEditor role={active} groups={groups} canEdit={canEdit} onSaved={load} />}
      </div>
      <NewRoleDialog open={open} onOpenChange={setOpen} onCreated={(r) => { setOpen(false); setActiveId(r.id); load(); }} />
    </div>
  );
}
